import {
  customElement,
  FASTElement,
  Observable,
  observable,
  Splice,
} from '@microsoft/fast-element';
import {
  Labels,
  projectModalTabs,
  ProjectModalTemplate as template,
} from './project-modal.template';
import { ProjectModalStyles as styles } from './project-modal.styles';
import { Tabs } from '@genesislcap/foundation-zero';
import Highcharts from 'highcharts';
import { GeneralTab, IProjectOperationalStatus } from './tabs/general/general-tab';
import { LocationTab } from './tabs/location/location-tab';
import { IProjectModalService, IProjectService } from '../../../services';
import { ContractsTab } from './tabs/contracts-summaries/contracts-summaries';
import { ConsultantTab } from './tabs/consultant-reports/consultant-reports';
import { PermitsTab } from './tabs/permits-government-approvals/permits-government-approvals';
import { Documents, FieldValidation, ObjectValidation, Project } from '../../../types';
import { MarketInfoTab } from './tabs/market-info/market-info-tab';
import { RightSideModal } from '../../right-side-modal/right-side-modal';
import { FileUpload } from '../../common/file-upload/file-upload';
import { ValidationColumnConfig } from '../../common/validation-grid/validation-grid';
import { logger } from '@genesislcap/foundation-comms';
import { PROJECT_OPERATIONAL_STATUS } from '../../../utils';
import { notifyError } from '../../../utils/notify';

const name = 'project-modal';

export enum projectFieldsType {
  AVAILABLE_CAPACITY_AC = 'number',
  CITY_NAME = 'string',
  COUNTRY_NAME = 'string',
  COUNTY_NAME = 'string',
  CREATED_BY = 'string',
  GUARANTEED_COD = 'number',
  HUB_HEIGHT = 'number',
  INTERCONNECTION_POINT = 'string',
  INTERCONNECTION_QUEUE_ID = 'string',
  INTERCONNECTION_VOLTAGE = 'string',
  INTERCONNECTION_UTILITY = 'string',
  ISO = 'string',
  LATITUDE = 'number',
  LEGAL_NAME = 'string',
  LONGITUDE = 'number',
  MEGAWATT_AC = 'number',
  MEGAWATT_DC = 'number',
  NAME = 'string',
  OWNER = 'string',
  P50_CAPACITY_FACTOR = 'number',
  PANEL_TECHNOLOGY = 'string',
  PANEL_TYPE = 'string',
  PANEL_MAKE_AND_MODEL = 'string',
  PROJECT_ID = 'string',
  PROJECT_OPERATOR = 'string',
  HUB_ZONE_MATRIX_ID = 'string',
  SETTLEMENT_POINT = 'string',
  STATE_NAME = 'string',
  STATE_ABBREVIATION = 'string',
  TARGET_COD = 'number',
  COD = 'number',
  TECHNOLOGY = 'string',
  TRACKING_TYPE = 'string',
  TURBINE_COUNT = 'number',
  TURBINE_DIAMETER = 'number',
  TURBINE_MAKE_AND_MODEL = 'string',
  TURBINE_MEGAWATT = 'number',
  CREATED_AT = 'number',
  MODIFIED_BY = 'string',
  MODIFIED_AT = 'number',
}

@customElement({
  name,
  template,
  styles,
})
export class ProjectModal extends FASTElement {
  @IProjectModalService projectModalService: IProjectModalService;
  @IProjectService projectService: IProjectService;

  @observable project: Project = {} as Project;
  @observable modalMode: 'create' | 'edit' = 'create';
  @observable documents = {} as Documents;
  @observable showModal: boolean = false;
  @observable generalTabForm: GeneralTab;
  @observable locationTabForm: LocationTab;
  @observable marketInfoTabForm: MarketInfoTab;
  @observable contractTabForm: ContractsTab;
  @observable consultantTabForm: ConsultantTab;
  @observable permitsTabForm: PermitsTab;

  // Used to mark the tab as completed and show the tick on the tab
  @observable generalCompleted: boolean;
  @observable locationCompleted: boolean;
  @observable marketInfoCompleted: boolean;
  @observable permitsTabCompleted: boolean;
  @observable contractsTabCompleted: boolean;
  @observable consultantTabCompleted: boolean;

  @observable projectModal: RightSideModal;
  @observable isFormValid: boolean = false;
  @observable isFormStarted: boolean = false;

  @observable title = Labels.TITLE;
  @observable file: any = undefined;
  @observable selectedTab: projectModalTabs = projectModalTabs.FORM;
  @observable isFileConfirmationState: boolean = false;
  @observable isRemodelOperationalProjectSelectStage: boolean = false;
  @observable correctProjectObjectsUploaded: Array<ObjectValidation> = [];
  @observable incorrectProjectObjectsUploaded: Array<ObjectValidation> = [];

  @observable confirmChangeToOperational: boolean = false;
  @observable confirmModalHeader: string;
  @observable confirmModalDescription: string;
  @observable remodelProjectId: string;

  public modalTabs: Tabs;
  public singleTab: HTMLElement;
  public batchTab: HTMLElement;
  public projectFieldCategories: Tabs;
  public chart;
  private totalRequiredFields = 0;
  private completedFields = 0;
  private notifier1: any;
  private handler1: any;
  private notifier2: any;
  private handler2: any;
  public fileUpload: FileUpload;

  @observable completedChart!: HTMLDivElement;
  @observable isUpdateModal: boolean = false;
  @observable projectColumns: Array<ValidationColumnConfig> = [];
  @observable projectOperationalStatus: PROJECT_OPERATIONAL_STATUS;

  @observable data: any[] = [
    {
      name: 'Completed',
      y: 0,
      color: '#A9FF78',
    },
    {
      name: 'Total',
      y: 100,
      color: '#3F3F3F',
    },
  ];

  connectedCallback() {
    super.connectedCallback();
    this.chartItColumnTotals();
    this.totalRequiredFields = this.projectModalService.requiredFormFields.length;
    this.completedFieldsListener();
    this.requiredFieldsListener();
    this.loadColumns();
  }

  private loadColumns() {
    this.projectColumns.push({ fieldName: 'NAME', headerName: 'Name', flex: 2 });
    this.projectColumns.push({ fieldName: 'TECHNOLOGY', headerName: 'Type', flex: 2 });
    this.projectColumns.push({
      fieldName: 'SETTLEMENT_POINT',
      headerName: 'Interconnection Region',
      flex: 3,
    });
    this.projectColumns.push({ fieldName: 'TARGET_COD', headerName: 'COD', flex: 2 });
    this.projectColumns.push({ fieldName: 'COUNTRY_NAME', headerName: 'Country', flex: 2 });
  }

  public async openModal() {
    this.projectModalService.requiredFormFields.splice(
      0,
      this.projectModalService.requiredFormFields.length,
    );
    this.showModal = true;
    this.projectOperationalStatus = PROJECT_OPERATIONAL_STATUS.OPERATIONAL;
    this.title = this.projectModalService.modalMode === 'edit' ? Labels.TITLE_EDIT : Labels.TITLE;

    this.fileUpload?.cleanData();

    if (this.showModal && this.modalTabs) {
      // Reset state of the top modal Tabs to show form rather than Batch upload
      this.modalTabs.activeid = '0';
      this.selectTab(projectModalTabs.FORM);
    }

    if (this.showModal && this.generalTabForm && this.locationTabForm && this.marketInfoTabForm) {
      if (this.projectModalService.modalMode === 'create') {
        this.locationTabForm.countryValue = 'United States';
        setTimeout(() => {
          this.locationTabForm.countrySelected();
          this.locationTabForm.checkFieldValidity(this.locationTabForm.country);
        }, 100);
      }
      this.generalTabForm.addRequiredFieldsToService();
      this.locationTabForm.addRequiredFieldsToService();
      this.marketInfoTabForm.addRequiredFieldsToService();
    }
  }

  closeModal() {
    this.confirmModalHeader = null;
    this.confirmModalDescription = null;
    this.title = Labels.TITLE;
    this.fileUpload?.cleanData();
    this.isFileConfirmationState = false;
    this.file = undefined;
    this.correctProjectObjectsUploaded?.splice(0);
    this.incorrectProjectObjectsUploaded?.splice(0);
    this.isUpdateModal = false;
    this.showModal = false;
    this.project = {} as Project;
    this.clearAllForms();
  }

  public selectTab(tab: projectModalTabs) {
    this.selectedTab = tab;
    if (this.selectedTab === projectModalTabs.FORM) {
      this.singleTab.hidden = false;
    } else {
      this.singleTab.hidden = true;
    }
  }

  public fileSelected(file: any) {
    this.file = file;
    if (this.isFileConfirmationState) {
      this.submitFile();
    }
  }

  public clearFileArea() {
    if (this.file) {
      this.fileUpload?.cleanData();
      this.file = undefined;
    }
  }

  public async submitFile() {
    this.fileUpload.setEndpoint('file-handler/upload-project');

    if (this.file) {
      const response = await this.projectService.sendProjectCsv(
        this.file,
        this.fileUpload.getEndpointUrl(this.fileUpload.endpoint),
      );

      this.correctProjectObjectsUploaded = response.objects?.filter(
        (object) => !object.objectHasProblem,
      );
      this.incorrectProjectObjectsUploaded = response.objects?.filter(
        (object) => object.objectHasProblem,
      );
      this.isFileConfirmationState = true;
      this.title = Labels.CONFIRM_PROJECT_UPLOAD_TITLE;
    }
  }

  public async insertProjectsList() {
    if (this.isFileConfirmationState && !this.incorrectProjectObjectsUploaded.length) {
      const projectResponseList = [];
      this.correctProjectObjectsUploaded.forEach((project) => {
        const projectResponse: Project = {
          AVAILABLE_CAPACITY_AC: 0,
          CITY_NAME: '',
          COUNTRY_NAME: '',
          COUNTY_NAME: '',
          CREATED_BY: '',
          GUARANTEED_COD: 0,
          HUB_HEIGHT: 0,
          INTERCONNECTION_POINT: '',
          INTERCONNECTION_QUEUE_ID: '',
          INTERCONNECTION_VOLTAGE: '',
          INTERCONNECTION_UTILITY: '',
          ISO: '',
          LATITUDE: 0,
          LEGAL_NAME: '',
          LONGITUDE: 0,
          MEGAWATT_AC: 0,
          MEGAWATT_DC: 0,
          NAME: '',
          OWNER: '',
          P50_CAPACITY_FACTOR: 0,
          PANEL_TECHNOLOGY: '',
          PANEL_TYPE: '',
          PANEL_MAKE_AND_MODEL: '',
          PROJECT_ID: null,
          PROJECT_OPERATOR: '',
          HUB_ZONE_MATRIX_ID: '',
          SETTLEMENT_POINT: '',
          STATE_NAME: '',
          STATE_ABBREVIATION: '',
          TARGET_COD: 0,
          COD: 0,
          TECHNOLOGY: 'WIND',
          TRACKING_TYPE: '',
          TURBINE_COUNT: 0,
          TURBINE_DIAMETER: 0,
          TURBINE_MAKE_AND_MODEL: '',
          TURBINE_MEGAWATT: 0,
          CREATED_AT: 0,
          MODIFIED_BY: '',
          MODIFIED_AT: 0,
          DRAFT_PROJECT_ID: '',
          TRACKING_TYPE_STRING: '',
          PANEL_TYPE_STRING: '',
          PANEL_TECHNOLOGY_STRING: '',
          ISO_STRING: '',
          INTERCONNECTION_VOLTAGE_STRING: '',
          PROJECT_OPERATIONAL_STATUS: 'NEW_BUILD',
          PROJECT_STATUS: 'NO_CONTRACTS_LISTED',
          CASCADE_COMPANY_ID: '',
          REMODEL_PROJECT_ID: '',
        };

        project.fields.map((field) => {
          return this.updateProjectField(field, projectResponse);
        });

        projectResponseList.push(projectResponse);
      });

      this.projectService.eventProjectsInsert(projectResponseList).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.projectModal.closeDialog();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response.ERROR);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
    }
  }

  updateProjectField(field: FieldValidation, projectResponse: Project) {
    if (projectFieldsType[field.fieldName] === 'string') {
      projectResponse[field.fieldName] = field.fieldValue;
    } else if (projectFieldsType[field.fieldName] === 'number') {
      projectResponse[field.fieldName] = field.fieldValue;
    } else if (projectFieldsType[field.fieldName] === 'date') {
      projectResponse[field.fieldName] = field.fieldValue;
    }
  }

  public cancelFileUpload() {
    this.closeModal();
    this.projectModal.closeDialog();
  }

  private chartItColumnTotals() {
    this.chart = Highcharts.chart({
      chart: {
        renderTo: this.completedChart,
        type: 'pie',
        backgroundColor: 'transparent',
        width: 170,
        height: 130,
      },
      tooltip: {
        enabled: false,
      },
      exporting: {
        buttons: null,
      },
      title: {
        text: null,
      },
      subtitle: {
        text: this.data[0].y + '%',
        align: 'center',
        verticalAlign: 'middle',
        style: {
          'font-size': '19px',
          color: 'white',
        },
        y: 7,
        x: 2,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          size: 106,
          shadow: false,
          dataLabels: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
      series: [
        {
          type: 'pie',
          data: this.data,
          innerSize: 86,
          borderColor: null,
        },
      ],
      legend: { enabled: false },
    });
  }

  public submitInsert() {
    this.getAllFormValues();
    if (
      this.generalTabForm.projectOperationalStatusValue === PROJECT_OPERATIONAL_STATUS.OPERATIONAL
    ) {
      this.isRemodelOperationalProjectSelectStage = true;
      this.title = Labels.CONFIRM_NEW_PROJECT;
      return;
    }
    this.submitForm();
  }

  private submitForm() {
    this.projectModalService.eventProjectInsert(this.project).then(
      (projectResponse) => {
        if (projectResponse && projectResponse.MESSAGE_TYPE === 'EVENT_ACK') {
          if (this.project.DRAFT_PROJECT_ID) {
            this.projectModalService.eventDraftProjectDelete(this.project).then((response) => {
              if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
              } else {
                notifyError(response.ERROR[0].TEXT);
                logger.error(response);
              }
            });
          }

          this.updateDocuments(projectResponse.GENERATED[0].PROJECT_ID);
        } else {
          notifyError(projectResponse.ERROR[0].TEXT);
          logger.error(projectResponse.ERROR[0].TEXT);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      },
    );
  }

  public editProject() {
    this.getAllFormValues();

    this.projectModalService.eventProjectUpdate(this.project).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.updateDocuments(this.project.PROJECT_ID);
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      },
    );
  }

  public updateDocuments(projectID: string) {
    if (this.project.PROJECT_OPERATIONAL_STATUS !== PROJECT_OPERATIONAL_STATUS.OPERATIONAL) {
      this.projectModalService
        .eventDocumentsInsert(projectID, this.documents)
        .then((documentsResponse) => {
          if (documentsResponse && documentsResponse.MESSAGE_TYPE === 'EVENT_ACK') {
            this.projectModal.closeDialog();
          } else {
            notifyError(documentsResponse.ERROR[0].TEXT);
            logger.error(documentsResponse.ERROR);
          }
        });
    } else {
      this.projectModal.closeDialog();
    }
  }

  public saveDraftDocuments(draftProjectId: string) {
    if (this.project.PROJECT_OPERATIONAL_STATUS !== PROJECT_OPERATIONAL_STATUS.OPERATIONAL) {
      for (const [key, value] of Object.entries(this.documents)) {
        if (!value) {
          this.documents[key] = undefined;
        }
      }

      this.projectModalService
        .eventDraftDocumentsUpsert(draftProjectId, this.documents)
        .then((documentsResponse) => {
          if (documentsResponse && documentsResponse.MESSAGE_TYPE === 'EVENT_ACK') {
            this.projectModal.closeDialog();
          } else {
            notifyError(documentsResponse.ERROR[0].TEXT);
            logger.error(documentsResponse.ERROR);
          }
        });
    } else {
      this.projectModal.closeDialog();
    }
  }

  public saveDraft() {
    this.getAllFormValues();
    this.projectModalService.eventDraftProjectUpsert(this.project).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.saveDraftDocuments(response.GENERATED[0].DRAFT_PROJECT_ID);
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      },
    );
  }

  private getAllFormValues() {
    this.project.CREATED_BY = localStorage.getItem('username');
    this.project.CREATED_AT = this.project.MODIFIED_AT = new Date();
    this.project.MODIFIED_BY = localStorage.getItem('username');
    this.project.CASCADE_COMPANY_ID = '';
    this.getGeneralTabValues();
    this.getLocationTabValues();
    this.getMarketInfoTabValues();
    this.getDocumentsValues();
  }

  public updateChart() {
    // Calculate the percentage of fields that are completed
    let completedPercentage = Math.round((100 * this.completedFields) / this.totalRequiredFields);

    if (this.totalRequiredFields == 0) {
      completedPercentage = 0;
    }

    this.data = [
      {
        name: 'Completed',
        y: completedPercentage,
        color: '#A9FF78',
      },
      {
        name: 'Total',
        y: 100 - completedPercentage,
        color: '#3F3F3F',
      },
    ];

    // Enable Create New Project button if form 100% completed
    this.isFormValid = completedPercentage == 100;
    this.isFormStarted = completedPercentage > 0;

    // Update the chart and subtitle with the values
    this.chart.series[0].setData(this.data);
    this.chart.setSubtitle({
      text: this.data[0].y + '%',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        'font-size': '19px',
        color: 'white',
      },
      y: 19,
      x: 0,
    });
  }

  private completedFieldsListener() {
    const _this = this;
    this.notifier1 = Observable.getNotifier(this.projectModalService.totalCompletedFields);
    this.handler1 = {
      handleChange(source: any, splices: Splice[]) {
        _this.completedFields = source.length;
        _this.updateChart();
      },
    };

    this.notifier1.subscribe(this.handler1);
  }

  public requiredFieldsListener() {
    const _this = this;
    this.notifier2 = Observable.getNotifier(this.projectModalService.requiredFormFields);
    this.handler2 = {
      handleChange(source: any, splices: Splice[]) {
        _this.totalRequiredFields = source.length;
        _this.updateChart();
      },
    };

    this.notifier2.subscribe(this.handler2);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.projectModalService.clearFields();
    this.notifier1.unsubscribe(this.handler1);
    this.notifier2.unsubscribe(this.handler2);
  }

  private getGeneralTabValues() {
    this.project.NAME = this.generalTabForm.projectNameValue;
    this.project.LEGAL_NAME = this.generalTabForm.legalNameValue;
    this.project.OWNER = this.generalTabForm.ownerValue;
    this.project.PROJECT_OPERATOR = this.generalTabForm.operatorValue;
    this.project.TECHNOLOGY = this.generalTabForm.techTypeValue;
    this.project.P50_CAPACITY_FACTOR = parseFloat(this.generalTabForm.capacityFactorValue);
    this.project.PROJECT_OPERATIONAL_STATUS = this.generalTabForm.projectOperationalStatusValue;

    if (this.project.TECHNOLOGY === 'WIND') {
      this.project.MEGAWATT_AC = parseFloat(this.generalTabForm.windCapacityMWACValue);
      this.project.AVAILABLE_CAPACITY_AC = parseFloat(
        this.generalTabForm.availableCapacityMWACValue,
      );
      this.project.TURBINE_MAKE_AND_MODEL = this.generalTabForm.turbineMakeModelValue;
      this.project.TURBINE_COUNT = parseFloat(this.generalTabForm.turbineCountValue);
      this.project.HUB_HEIGHT = parseFloat(this.generalTabForm.hubHeightValue);
      this.project.TURBINE_MEGAWATT = parseFloat(this.generalTabForm.turbineNameplateValue);
      this.project.TURBINE_DIAMETER = parseFloat(this.generalTabForm.turbineDiameterValue);
    } else if (this.project.TECHNOLOGY === 'SOLAR') {
      this.project.MEGAWATT_DC = parseFloat(this.generalTabForm.solarCapacityMWDCValue);
      this.project.MEGAWATT_AC = parseFloat(this.generalTabForm.solarCapacityMWACValue);
      this.project.AVAILABLE_CAPACITY_AC = parseFloat(
        this.generalTabForm.solarAvailableCapacityMWACValue,
      );
      this.project.PANEL_TYPE = this.generalTabForm.panelTypeValue;
      this.project.PANEL_TYPE_STRING = this.generalTabForm.panelTypeValue;
      this.project.PANEL_TECHNOLOGY = this.generalTabForm.panelTechnologyValue;
      this.project.PANEL_TECHNOLOGY_STRING = this.generalTabForm.panelTechnologyValue;
      this.project.PANEL_MAKE_AND_MODEL = this.generalTabForm.panelMakeModelValue;
      this.project.TRACKING_TYPE = this.generalTabForm.trackingTypeValue;
      this.project.TRACKING_TYPE_STRING = this.generalTabForm.trackingTypeValue;
      // Clear wind values
      this.project.TURBINE_MAKE_AND_MODEL = '';
      this.project.TURBINE_COUNT = null;
      this.project.HUB_HEIGHT = null;
      this.project.TURBINE_MEGAWATT = null;
      this.project.TURBINE_DIAMETER = null;
    }
  }

  private getLocationTabValues() {
    this.project.COUNTRY_NAME = this.locationTabForm.countryValue;
    this.project.LATITUDE = parseFloat(this.locationTabForm.latitudeValue);
    this.project.LONGITUDE = parseFloat(this.locationTabForm.longitudeValue);

    if (this.project.COUNTRY_NAME === 'United States') {
      // Parse string back to Object to get both STATE_NAME and STATE_ABBREVIATION
      if (this.locationTabForm.stateValue) {
        const selectedState = JSON.parse(this.locationTabForm.stateValue);
        this.project.STATE_NAME = selectedState.STATE_NAME;
        this.project.STATE_ABBREVIATION = selectedState.STATE_ABBREVIATION;
      }

      this.project.COUNTY_NAME = this.locationTabForm.countyValue;
      this.project.CITY_NAME = this.locationTabForm.cityValue;
    } else {
      this.project.STATE_NAME = '';
      this.project.STATE_ABBREVIATION = '';
      this.project.COUNTY_NAME = '';
      this.project.CITY_NAME = '';
    }
  }

  private getMarketInfoTabValues() {
    this.project.ISO = this.marketInfoTabForm.isoValue;
    this.project.ISO_STRING = this.marketInfoTabForm.isoValue;
    this.project.SETTLEMENT_POINT = this.marketInfoTabForm.settlementPointValue;
    this.project.SETTLEMENT_POINT = this.marketInfoTabForm.settlementPointValue;

    // Parse string back to object to get both settlementIndex and hubZoneMatrixID
    if (this.marketInfoTabForm.settlementPointValue) {
      const selectedMatrix = this.marketInfoTabForm._hubZoneMatrix.find((item) => {
        return (
          item.ISO === this.marketInfoTabForm.isoValue &&
          item.SETTLEMENT_POINT === this.marketInfoTabForm.settlementPointValue &&
          item.SETTLEMENT_INDEX === 'DAY_AHEAD'
        );
      });
      this.project.HUB_ZONE_MATRIX_ID = selectedMatrix?.HUB_ZONE_MATRIX_ID;
    }

    this.project.INTERCONNECTION_POINT = this.marketInfoTabForm.interPoint.autofillValueID;
    this.project.INTERCONNECTION_VOLTAGE = this.marketInfoTabForm.interVoltageValue;
    this.project.INTERCONNECTION_UTILITY = this.marketInfoTabForm.interUtilityValue;
    this.project.INTERCONNECTION_VOLTAGE_STRING = this.marketInfoTabForm.interVoltageValue;
    this.project.TARGET_COD = new Date(this.marketInfoTabForm.targetCODValue);
    this.project.GUARANTEED_COD = new Date(this.marketInfoTabForm.guarantedCODValue);
    this.project.COD = new Date(this.marketInfoTabForm.operationalProjectCODValue);
    this.project.INTERCONNECTION_QUEUE_ID = this.marketInfoTabForm.interQueueIdValue;
  }

  private getDocumentsValues() {
    this.documents.EQUIPMENT_SUPPLY_AGREEMENTS =
      this.contractTabForm.equipmentSupplyAgreements.value;
    this.documents.EPC_AGREEMENT = this.contractTabForm.epcAgreement.value;
    this.documents.OPERATIONS_MAINTENANCE_AGREEMENT =
      this.contractTabForm.operationMaintenanceAgreement.value;
    this.documents.MANAGEMENT_SERVICES_AGREEMENT =
      this.contractTabForm.managementServicesAgreement.value;

    this.documents.FEASIBILITY_STUDY = this.contractTabForm.feasibilityStudy.value;
    this.documents.SYSTEM_IMPACT_STUDY = this.contractTabForm.systemImpactStudy.value;
    this.documents.FACILITIES_STUDY = this.contractTabForm.facilitiesStudy.value;

    this.documents.INTERCONNECTION_AGREEMENT = this.contractTabForm.interconnectionAgreement.value;
    this.documents.BOP_AGREEMENT = this.contractTabForm.bopAgreement.value;
    this.documents.CONSTRUCTION_AGREEMENT = this.contractTabForm.constructionAgreement.value;
    this.documents.ENERGY_MANAGEMENT_AGREEMENT =
      this.contractTabForm.energyManagementAgreement.value;

    this.documents.INDEPENDENT_ENGINEERING_REPORT =
      this.consultantTabForm.independentEngReport.value;
    this.documents.ENERGY_RESOURCE_STUDY = this.consultantTabForm.energyResourceStudy.value;
    this.documents.TRANSMISSION_CONGESTION_STUDY = this.consultantTabForm.transmissionStudy.value;
    this.documents.INSURANCE_CONSULTANT_REPORT =
      this.consultantTabForm.insuranceConsultantReport.value;

    this.documents.LAND_CONTROL_AGREEMENTS = this.permitsTabForm.landControlAgreements.value;
    this.documents.TITLE_INSURANCE = this.permitsTabForm.titleInsurance.value;
    this.documents.LITIGATION_DISCLOSURE = this.permitsTabForm.litigationDisclosure.value;
    this.documents.LAND_TITLE_SURVEY = this.permitsTabForm.landTitleAssociationSurvey.value;
    this.documents.FLOOD_HAZARD_DETERMINATION = this.permitsTabForm.emergencyManagement.value;
    this.documents.ESA_REVIEW = this.permitsTabForm.phaseIEnvironmentalReview.value;
    this.documents.WETLANDS_DELINEATION_REVIEW =
      this.permitsTabForm.wetlandsDelineationReview.value;
    this.documents.THREATENED_ENDANGERED_SPECIES_REVIEW = this.permitsTabForm.speciesReview.value;
    this.documents.CULTURAL_RESOURCES_STUDY_REVIEW =
      this.permitsTabForm.culturalResourcesStudyReview.value;
    this.documents.PRIMARY_STATE_LEVEL_PERMIT = this.permitsTabForm.primaryStateLevelPermit.value;
    this.documents.PRIMARY_COUNTY_LEVEL_PERMIT = this.permitsTabForm.primaryCountyLevelPermit.value;
    this.documents.PRIMARY_TOWNSHIP_LEVEL_PERMIT =
      this.permitsTabForm.primaryTownshipLevelPermit.value;
    this.documents.TAX_ABATEMENT_APPLICATION = this.permitsTabForm.taxAbatementApplication.value;
    this.documents.DNH_FROM_FAA = this.permitsTabForm.DnhFromFaa.value;
  }

  public async populateAllForms(project: Project) {
    let _projectID = '';
    let documents: Documents = null;
    let response = null;

    if (this.projectModalService.modalMode === 'edit') {
      this.project.PROJECT_ID = project.PROJECT_ID;
      _projectID = project.PROJECT_ID;
      response = await this.projectModalService.getDocument(_projectID);

      if (response && response.MESSAGE_TYPE !== 'EVENT_NACK') {
        // TODO: Update when we got the toast notificiations setup
        documents = response.REPLY[0];
      } else {
        notifyError(response.ERROR);
        logger.error(response.ERROR);
      }

      response = null;
    } else {
      // If you're populating these fields for Draft Project
      this.project.DRAFT_PROJECT_ID = project.DRAFT_PROJECT_ID;
      _projectID = project.DRAFT_PROJECT_ID;
      response = await this.projectModalService.getDraftDocument(_projectID);

      if (response && response.MESSAGE_TYPE !== 'EVENT_NACK') {
        // TODO: Update when we got the toast notificiations setup
        documents = response.REPLY[0];
      } else {
        notifyError(response.ERROR);
        logger.error(response.ERROR);
      }

      response = null;
    }

    this.generalTabForm.populateForm(project);
    this.locationTabForm.populateForm(project);
    this.marketInfoTabForm.populateForm(project);

    if (documents) {
      this.contractTabForm.populateForm(documents);
      this.consultantTabForm.populateForm(documents);
      this.permitsTabForm.populateForm(documents);
    }
  }

  public clearAllForms() {
    this.project.DRAFT_PROJECT_ID = null;
    this.project.PROJECT_ID = null;
    this.generalTabForm.clearForm();
    this.locationTabForm.clearForm();
    this.marketInfoTabForm.clearForm();
    this.contractTabForm.clearForm();
    this.consultantTabForm.clearForm();
    this.permitsTabForm.clearForm();
    this.projectModalService.totalCompletedFields.splice(
      0,
      this.projectModalService.totalCompletedFields.length,
    );
    this.projectModalService.requiredFormFields.splice(
      0,
      this.projectModalService.requiredFormFields.length,
    );
    this.isFileConfirmationState = false;
    this.isRemodelOperationalProjectSelectStage = false;
    this.remodelProjectId = null;
    this.projectFieldCategories.activeid = '0';
  }
  // clear method from new project
  public clearFormFromModal() {
    this.clearAllForms();
    this.generalTabForm.addRequiredFieldsToService();
    this.locationTabForm.addRequiredFieldsToService();
    this.marketInfoTabForm.addRequiredFieldsToService();
  }

  public updateProjectOperationalStatus(
    projectOperationalStatusCallbackObject: IProjectOperationalStatus,
  ) {
    this.confirmModalHeader = null;
    this.confirmModalDescription = null;
    if (projectOperationalStatusCallbackObject.shouldConfirm) {
      this.confirmModalHeader = 'Are you sure?';
      this.confirmModalDescription =
        'By changing the Project from New-Build to Operational, all entries for Contracts and Summaries, Consultant Reports, and Permits and Government Approvals will no longer be editable or visible as they should no longer be applicable. Please confirm you would like to Proceed.';

      this.projectModal.confirmDialog();
    } else {
      this.projectOperationalStatus =
        projectOperationalStatusCallbackObject.projectOperationalStatus;
    }
    if (this.projectOperationalStatus === PROJECT_OPERATIONAL_STATUS.OPERATIONAL) {
      this.contractTabForm.clearForm();
      this.consultantTabForm.clearForm();
      this.permitsTabForm.clearForm();

      this.permitsTabForm.removeRequiredFieldsToService();
      this.contractTabForm.removeRequiredFieldsToService();
      this.consultantTabForm.removeRequiredFieldsToService();
    } else {
      this.permitsTabForm.addRequiredFieldsToService();
      this.contractTabForm.addRequiredFieldsToService();
      this.consultantTabForm.addRequiredFieldsToService();
    }
  }

  public confirmModal(confirmOperationalChange: boolean) {
    this.confirmModalHeader = null;
    this.confirmModalDescription = null;
    if (confirmOperationalChange) {
      this.projectOperationalStatus = PROJECT_OPERATIONAL_STATUS.OPERATIONAL;
    } else {
      this.projectOperationalStatus = PROJECT_OPERATIONAL_STATUS.NEW_BUILD;
      this.generalTabForm.setProjectOperationalStatus(this.projectOperationalStatus);
    }
  }

  confirmSelectedProject() {
    this.project.REMODEL_PROJECT_ID = this.remodelProjectId;
    this.isRemodelOperationalProjectSelectStage = false;
    this.submitForm();
  }

  proceedWithModeledResult() {
    this.isRemodelOperationalProjectSelectStage = false;
    this.submitForm();
  }

  returnToProjectModalFromRemodel() {
    this.isRemodelOperationalProjectSelectStage = false;
    this.title = Labels.TITLE;
  }
}
